import { useMemo, useState, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Typography, Avatar } from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  AimOutlined,
  MailOutlined,
  RobotOutlined,
  SettingOutlined,
  ControlOutlined,
  LogoutOutlined,
  SafetyCertificateOutlined,
  SoundOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useAuth } from '@hooks/useAuth';
import { useTheme } from '@hooks/useTheme';
import { useAppSelector } from '@store/hooks';
import routesConfig from '@config/routes.config.json';
import type { RouteConfig } from '@appTypes/route.types';
import { canAccessRoute } from '@router/routeGuards';
import './sidebar.css';

const typedRoutes = routesConfig.routes as RouteConfig[];

function pathUnderParent(parentKey: string, pathname: string, routes: RouteConfig[]): boolean {
  const r = routes.find((x) => x.path === parentKey && x.children?.length);
  if (!r?.children) return false;
  return r.children.some((c) => pathname === c.path || pathname.startsWith(`${c.path}/`));
}

const { Sider } = Layout;
const { Text, Title } = Typography;

const iconMap: Record<string, React.ReactNode> = {
  DashboardOutlined: <DashboardOutlined />,
  FileTextOutlined: <FileTextOutlined />,
  AimOutlined: <AimOutlined />,
  MailOutlined: <MailOutlined />,
  RobotOutlined: <RobotOutlined />,
  SettingOutlined: <SettingOutlined />,
  ControlOutlined: <ControlOutlined />,
  SafetyCertificateOutlined: <SafetyCertificateOutlined />,
  SoundOutlined: <SoundOutlined />,
  CodeOutlined: <CodeOutlined />,
};

function buildMenuItems(routes: RouteConfig[], user: ReturnType<typeof useAuth>['user']): MenuProps['items'] {
  return routes
    .filter((r) => r.layout && !r.hideInMenu && canAccessRoute(user, r))
    .map((route) => {
      if (route.children && route.children.length > 0) {
        const visibleChildren = route.children.filter((c) => !c.hideInMenu && canAccessRoute(user, c));
        if (visibleChildren.length > 0) {
          return {
            key: route.path,
            icon: route.icon ? iconMap[route.icon] : undefined,
            label: route.label || route.key,
            children: visibleChildren.map((child) => ({
              key: child.path,
              label: child.label || child.key,
            })),
          };
        }
      }

      return {
        key: route.path,
        icon: route.icon ? iconMap[route.icon] : undefined,
        label: route.label || route.key,
      };
    });
}

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isDark } = useTheme();
  const collapsed = useAppSelector((state) => state.app.sidebarCollapsed);

  const menuItems = useMemo(
    () => buildMenuItems(routesConfig.routes as RouteConfig[], user),
    [user],
  );

  const selectedKeys = useMemo(() => {
    const path = location.pathname;
    const matched = typedRoutes.find(
      (r) => r.path === path || r.children?.some((c) => c.path === path),
    );
    return matched ? [path] : ['/'];
  }, [location.pathname]);

  const routeOpenKeys = useMemo(() => {
    const path = location.pathname;
    const parent = typedRoutes.find((r) => r.children?.some((c) => c.path === path));
    return parent ? [parent.path] : [];
  }, [location.pathname]);

  const [menuOpenKeys, setMenuOpenKeys] = useState<string[]>(routeOpenKeys);

  useLayoutEffect(() => {
    if (collapsed) {
      setMenuOpenKeys([]);
      return;
    }
    setMenuOpenKeys((prev) => {
      const next = new Set<string>(routeOpenKeys);
      for (const k of prev) {
        if (pathUnderParent(k, location.pathname, typedRoutes)) {
          next.add(k);
        }
      }
      return Array.from(next);
    });
  }, [location.pathname, routeOpenKeys, collapsed]);

  const handleOpenChange: MenuProps['onOpenChange'] = (keys) => {
    if (collapsed) {
      setMenuOpenKeys(keys);
      return;
    }
    setMenuOpenKeys([...new Set([...keys, ...routeOpenKeys])]);
  };

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    navigate(key);
  };

  return (
    <Sider
      width={260}
      collapsedWidth={72}
      collapsed={collapsed}
      className="pr-sidebar"
      style={{
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 200,
        borderRight: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        className="pr-sidebar-scroll"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'visible',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      <div style={{ padding: collapsed ? '20px 12px' : '20px 24px', textAlign: collapsed ? 'center' : 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src="/gen-logo.png"
            alt="Gen Logo"
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
          {!collapsed && (
            <Title level={5} style={{ margin: 0, whiteSpace: 'nowrap' }}>
              PR NewsCraft AI
            </Title>
          )}
        </div>
      </div>

      <Menu
        mode="inline"
        inlineCollapsed={collapsed}
        selectedKeys={selectedKeys}
        openKeys={menuOpenKeys}
        onOpenChange={handleOpenChange}
        getPopupContainer={() => document.body}
        items={menuItems}
        onClick={handleMenuClick}
        style={{ borderRight: 'none' }}
      />

      </div>

      <div
        style={{
          padding: collapsed ? '16px 12px' : '16px 24px',
          borderTop: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
          flexShrink: 0,
        }}
      >
        {!collapsed && user && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <Avatar style={{ background: '#6366f1' }}>
              {user.displayName.charAt(0).toUpperCase()}
            </Avatar>
            <div style={{ overflow: 'hidden' }}>
              <Text strong ellipsis style={{ display: 'block', fontSize: 13 }}>
                {user.displayName}
              </Text>
              <Text type="secondary" ellipsis style={{ display: 'block', fontSize: 11 }}>
                {user.email}
              </Text>
            </div>
          </div>
        )}
        <Menu
          mode="inline"
          selectable={false}
          items={[
            {
              key: 'settings',
              icon: <SettingOutlined />,
              label: collapsed ? '' : 'Settings',
              onClick: () => navigate('/settings'),
            },
            {
              key: 'logout',
              icon: <LogoutOutlined />,
              label: collapsed ? '' : 'Logout',
              danger: true,
              onClick: () => logout(),
            },
          ]}
          style={{ borderRight: 'none' }}
        />
      </div>
    </Sider>
  );
}
